import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FavoriteProductCard, useFavoritesStrip } from "@/features/favorites";
import { useCommercePaths } from "@/modules";

/**
 * Sepet sayfasındaki favori şeridi.
 *
 * Gerçek favori listesinden beslenir; oturum yoksa ya da favori yoksa bölüm
 * hiç çizilmez — boş bir şerit sepet sayfasında yer kaplamasın.
 */
export const FavoritesSection: React.FC = () => {
  const { t } = useTranslation();
  const paths = useCommercePaths();
  const { rows, isLoading, isHidden } = useFavoritesStrip();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  };

  if (isHidden) return null;

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <Link
          to={paths.favorites()}
          className="flex items-center gap-2 text-gray-900 transition-colors hover:text-module-accent dark:text-gray-100"
        >
          <h2 className="text-lg font-semibold md:text-xl">
            {t("common.favorites")}
          </h2>
          <ChevronRight className="hidden h-5 w-5 md:block md:h-6 md:w-6" />
        </Link>

        {/* Mobile Navigation Buttons */}
        <div className="flex items-center gap-2 md:hidden">
          <button
            onClick={scrollLeft}
            aria-label={t("common.previous")}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 transition-colors hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-4 w-4 text-gray-900 dark:text-gray-100" />
          </button>
          <button
            onClick={scrollRight}
            aria-label={t("common.next")}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-gray-100 transition-colors hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
          >
            <ChevronRight className="h-4 w-4 text-gray-900 dark:text-gray-100" />
          </button>
        </div>
      </div>

      {/* Products Strip */}
      <div
        ref={scrollContainerRef}
        className="scrollbar-hide flex gap-2 overflow-x-auto pb-2 md:gap-4"
      >
        {isLoading
          ? Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="w-[160px] flex-shrink-0 rounded-xl border border-gray-200 p-1.5 dark:border-gray-700 md:w-[224px] md:rounded-2xl md:p-2"
              >
                <div className="mb-2 aspect-square animate-pulse rounded-xl bg-gray-200 dark:bg-gray-700 md:mb-3" />
                <div className="flex flex-col gap-1.5 px-1 pb-1">
                  <div className="h-3 w-1/3 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                  <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                </div>
              </div>
            ))
          : rows.map((row) => (
              <FavoriteProductCard
                key={row.key}
                product={row.product}
                productId={row.productId}
                className="w-[160px] flex-shrink-0 md:w-[224px]"
              />
            ))}
      </div>
    </div>
  );
};
